(() => {
  const SETTINGS = {
    dateWrapperSelector: ".cake-date",
    rushWrapperSelector: ".need-by-select",

    standardOption: "Standard Turnaround",
    weekRushOption: "1 Week Rush",
    threeDayRushOption: "3 Day Rush",

    bakeryTimeZone: "America/New_York",
    cutoffHour: 10,
    cutoffMinute: 0,

    minimumLeadDays: 3,
    weekRushStarts: 7,
    standardStarts: 14,

    // Sunday = 0, Monday = 1
    closedPickupDays: [0, 1],

    pickupHours: "9:30 AM–2:30 PM"
  };

  const initializedFields = new WeakSet();

  function getBakeryDateTimeParts(date = new Date()) {
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: SETTINGS.bakeryTimeZone,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hourCycle: "h23"
    });

    const parts = formatter.formatToParts(date);
    const result = {};

    parts.forEach((part) => {
      if (part.type !== "literal") {
        result[part.type] = Number(part.value);
      }
    });

    return {
      year: result.year,
      month: result.month,
      day: result.day,
      hour: result.hour,
      minute: result.minute
    };
  }

  function parseSelectedDate(value) {
    if (!value) return null;

    let match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);

    if (match) {
      return {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3])
      };
    }

    match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);

    if (match) {
      return {
        year: Number(match[3]),
        month: Number(match[1]),
        day: Number(match[2])
      };
    }

    const parsed = new Date(value);

    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    return {
      year: parsed.getFullYear(),
      month: parsed.getMonth() + 1,
      day: parsed.getDate()
    };
  }

  function datePartsToDayNumber(parts) {
    return Math.floor(
      Date.UTC(parts.year, parts.month - 1, parts.day) / 86400000
    );
  }

  function getProductionStartDate() {
    const bakeryNow = getBakeryDateTimeParts();

    const start = new Date(
      Date.UTC(
        bakeryNow.year,
        bakeryNow.month - 1,
        bakeryNow.day
      )
    );

    /*
      Today counts through exactly 10:00 AM.
      Beginning at 10:01 AM, counting starts tomorrow.
    */
    const afterCutoff =
      bakeryNow.hour > SETTINGS.cutoffHour ||
      (
        bakeryNow.hour === SETTINGS.cutoffHour &&
        bakeryNow.minute > SETTINGS.cutoffMinute
      );

    if (afterCutoff) {
      start.setUTCDate(start.getUTCDate() + 1);
    }

    return {
      year: start.getUTCFullYear(),
      month: start.getUTCMonth() + 1,
      day: start.getUTCDate()
    };
  }

  function getWeekday(dateParts) {
    return new Date(
      Date.UTC(
        dateParts.year,
        dateParts.month - 1,
        dateParts.day
      )
    ).getUTCDay();
  }

  function calculateLeadDays(pickupDate) {
    const productionStart = getProductionStartDate();

    const difference =
      datePartsToDayNumber(pickupDate) -
      datePartsToDayNumber(productionStart);

    // Inclusive counting: production start is Day 1.
    return difference + 1;
  }

  function formatPickupDate(dateParts) {
    const date = new Date(
      Date.UTC(
        dateParts.year,
        dateParts.month - 1,
        dateParts.day,
        12
      )
    );

    return new Intl.DateTimeFormat("en-US", {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC"
    }).format(date);
  }

  function getDateRule(pickupDate) {
  const weekday = getWeekday(pickupDate);

  if (SETTINGS.closedPickupDays.includes(weekday)) {
    return {
      valid: false,
      message:
        "Pickup is not available on Sundays or Mondays. Please choose a pickup date between Tuesday and Saturday."
    };
  }

  const leadDays = calculateLeadDays(pickupDate);

  if (leadDays < SETTINGS.minimumLeadDays) {
    return {
      valid: false,
      message:
        "We're sorry! This pickup date is no longer available for online ordering. Please call us to ask whether we can accommodate your request."
    };
  }

  const formattedDate = formatPickupDate(pickupDate);

  if (leadDays >= SETTINGS.standardStarts) {
    return {
      valid: true,
      option: SETTINGS.standardOption,
      message:
        `Great choice! Your selected pickup date qualifies for our standard turnaround. No rush fee is required. ` +
        `Pickup is on ${formattedDate}, anytime between ${SETTINGS.pickupHours}.`
    };
  }

  if (leadDays >= SETTINGS.weekRushStarts) {
    return {
      valid: true,
      option: SETTINGS.weekRushOption,
      message:
        `Rush Order Selected: Your pickup date falls within our 7–13 day turnaround, so a $50 rush fee has been added automatically. ` +
        `Pickup is on ${formattedDate}, anytime between ${SETTINGS.pickupHours}.`
    };
  }

  return {
    valid: true,
    option: SETTINGS.threeDayRushOption,
    message:
      `Priority Rush Order: Your pickup date falls within our 3–6 day turnaround, so a $75 rush fee has been added automatically. ` +
      `Pickup is on ${formattedDate}, anytime between ${SETTINGS.pickupHours}.`
  };
  }

  function getOrCreateMessage(dateWrapper) {
    let message = dateWrapper.querySelector(".cake-date-rule-message");

    if (message) return message;

    message = document.createElement("div");
    message.className = "cake-date-rule-message";
    message.setAttribute("role", "status");
    message.setAttribute("aria-live", "polite");

    dateWrapper.appendChild(message);

    return message;
  }

  function getProductForm(element) {
    return (
      element.closest('form[action*="/cart/add"]') ||
      document.querySelector('form[action*="/cart/add"]')
    );
  }

  function getAddToCartButtons(form) {
    if (!form) return [];

    return Array.from(
      form.querySelectorAll(
        'button[type="submit"], input[type="submit"], [name="add"]'
      )
    );
  }

  function setAddToCartEnabled(form, enabled) {
    getAddToCartButtons(form).forEach((button) => {
      button.disabled = !enabled;
      button.setAttribute("aria-disabled", String(!enabled));
    });
  }

  function setRushOption(select, optionText) {
    const normalizedTarget = optionText
      .trim()
      .toLowerCase();

    const matchingOption = Array.from(select.options).find((option) => {
      const optionLabel = option.textContent
        .trim()
        .toLowerCase();

      const optionValue = String(option.value)
        .trim()
        .toLowerCase();

      return (
        optionLabel === normalizedTarget ||
        optionValue === normalizedTarget
      );
    });

    if (!matchingOption) {
      console.error(
        `[Cake pickup rules] Could not find the option "${optionText}".`,
        Array.from(select.options).map((option) =>
          option.textContent.trim()
        )
      );

      return false;
    }

    select.value = matchingOption.value;

    select.dispatchEvent(
      new Event("input", {
        bubbles: true
      })
    );

    select.dispatchEvent(
      new Event("change", {
        bubbles: true
      })
    );

    return true;
  }

  function displayMessage(messageElement, message, valid) {
    messageElement.textContent = message;
    messageElement.classList.toggle(
      "cake-date-rule-message--valid",
      valid
    );
    messageElement.classList.toggle(
      "cake-date-rule-message--invalid",
      !valid
    );
    messageElement.hidden = false;
  }

  function initializeCakeDateRules() {
    const dateWrappers = document.querySelectorAll(
      SETTINGS.dateWrapperSelector
    );

    dateWrappers.forEach((dateWrapper) => {
      const dateInput = dateWrapper.querySelector("input");

      const surroundingArea =
        dateWrapper.closest("form") ||
        dateWrapper.parentElement ||
        document;

      const rushWrapper =
        surroundingArea.querySelector(
          SETTINGS.rushWrapperSelector
        ) ||
        document.querySelector(
          SETTINGS.rushWrapperSelector
        );

      const rushSelect = rushWrapper?.querySelector("select");

      if (!dateInput || !rushSelect) return;
      if (initializedFields.has(dateInput)) return;

      initializedFields.add(dateInput);

      const form = getProductForm(dateInput);
      const messageElement = getOrCreateMessage(dateWrapper);

      function validateDate() {
        const pickupDate = parseSelectedDate(dateInput.value);

        if (!pickupDate) {
          messageElement.hidden = true;
          setAddToCartEnabled(form, false);
          return false;
        }

        const rule = getDateRule(pickupDate);

        displayMessage(
          messageElement,
          rule.message,
          rule.valid
        );

        if (!rule.valid) {
          setAddToCartEnabled(form, false);
          return false;
        }

        const optionApplied = setRushOption(
          rushSelect,
          rule.option
        );

        setAddToCartEnabled(form, optionApplied);

        return optionApplied;
      }

      dateInput.addEventListener("change", validateDate);
      dateInput.addEventListener("input", validateDate);
      dateInput.addEventListener("blur", validateDate);

      /*
        Customers can see the automatic selection during testing,
        but cannot manually change it.
      */
      rushSelect.addEventListener("change", () => {
        if (dateInput.value) {
          window.setTimeout(validateDate, 0);
        }
      });

      form?.addEventListener(
        "submit",
        (event) => {
          if (!validateDate()) {
            event.preventDefault();
            event.stopImmediatePropagation();

            messageElement.scrollIntoView({
              behavior: "smooth",
              block: "center"
            });
          }
        },
        true
      );

      setAddToCartEnabled(form, false);

      if (dateInput.value) {
        validateDate();
      }
    });
  }

  initializeCakeDateRules();

  const observer = new MutationObserver(() => {
    initializeCakeDateRules();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });

  document.addEventListener(
    "shopify:section:load",
    initializeCakeDateRules
  );
})();